/**
 * React Query Hook: Get Credit History
 *
 * Hook for fetching paginated credit transaction history.
 * Endpoint: GET /api/credits/history
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getCreditHistory } from "@/api/endpoints/credits";
import { queryKeys } from "@/lib/queryClient";
import type { GetCreditHistoryResponse } from "@/api/types/credits";

/**
 * Hook to get credit transaction history
 *
 * Features:
 * - Paginated results with limit and offset
 * - Keeps previous data while fetching new page (smooth pagination)
 * - Cached with unique keys for each page
 * - Stale time: 5 minutes (history doesn't change often)
 *
 * @param limit - Number of transactions per page (default: 10)
 * @param offset - Offset for pagination (default: 0)
 * @returns React Query result with transaction history
 */
export function useGetCreditHistory(
  limit = 10,
  offset = 0
): UseQueryResult<GetCreditHistoryResponse, Error> {
  return useQuery({
    queryKey: queryKeys.credits.history(limit, offset),
    queryFn: () => getCreditHistory(limit, offset),
    staleTime: 1000 * 60 * 5, // 5 minutes - history doesn't change often
    placeholderData: (previousData) => previousData, // Keep old data while fetching
  });
}
